import React, { useState, useEffect, useRef } from 'react';
import { Play, Square, X } from 'lucide-react';
import toast from 'react-hot-toast';
import { formatCryptoIDR, formatIDRWithDecimals } from '../utils/currency';
import { cn } from '../utils/cn';
import { useCoinData } from '../contexts/CoinDataContext';
import { csrfFetch } from '../utils/csrfFetch';
import { generateIdempotencyKey } from '../utils/idempotency';
import { buildApiUrl } from '../config';
import { useTradeStatusHub } from '../hooks/useTradeStatusHub';

const parseJsonSafely = async (response) => {
  try {
    return await response.json();
  } catch (_) {
    return null;
  }
};

const isResponseSuccess = (response, data) => {
  if (data && Object.prototype.hasOwnProperty.call(data, 'success')) {
    return data.success === 1 || data.success === true;
  }
  return response.ok;
};

// Cap the number of unresolved (in-flight) spam-sell requests. The 60ms timer already
// keeps the *send rate* at ~16/s; this bounds the *pile-up* when Indodax lags, so a
// recovery burst can't blow past Indodax's 20 req/s-per-pair limit. Lower = safer.
const MAX_IN_FLIGHT = 16;

// When Indodax rejects us for rate limiting, pause this long before sending again
// (overridden by the server's Retry-After header when it provides one).
const RATE_LIMIT_COOLDOWN_MS = 1000;

const SpamSell = ({ mode = 'legacy' }) => {
  const { coinData } = useCoinData();
  const isWorkerMode = mode === 'worker';
  const startEndpoint = isWorkerMode
    ? '/exchange/indodax/trade/worker/start'
    : '/exchange/indodax/trade';
  const cancelEndpoint = '/exchange/indodax/trade/worker/cancel';
  const {
    isConnected: isStatusConnected,
    isRunning: statusRunning,
    tradeUpdate,
    requestStatus,
  } = useTradeStatusHub({ enabled: isWorkerMode });
  const [selectedBlockchain, setSelectedBlockchain] = useState('');
  const [interval, setInterval] = useState(60);
  const [volume, setVolume] = useState(0);
  const [price, setPrice] = useState('');
  const [isRunning, setIsRunning] = useState(false);
  const [idrBalance, setIdrBalance] = useState(0);
  const [availableCoins, setAvailableCoins] = useState([]);
  const [logs, setLogs] = useState([]);
  const [showLogs, setShowLogs] = useState(false);
  const [showCoinDialog, setShowCoinDialog] = useState(false);
  const [coinSearch, setCoinSearch] = useState('');

  const [sellOrders, setSellOrders] = useState([]);
  const [buyOrders, setBuyOrders] = useState([]);
  const isRunningRef = useRef(false);
  const timerRef = useRef(null);
  const inFlightRef = useRef(0);
  const cooldownUntilRef = useRef(0);
  const prevSellRef = useRef([]);
  const prevBuyRef = useRef([]);
  const prevStatusRef = useRef(null);
  const prevTradeUpdateAtRef = useRef(null);

  const formatWithDots = (value, options = {}) => {
    const num = Number(value);
    if (!Number.isFinite(num)) return value ?? '-';
    return new Intl.NumberFormat('id-ID', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
      ...options,
    }).format(num);
  };

  // Fetch orderbook data from Indodax API
  const fetchOrderbookData = async (pairId = 'btcidr') => {
    if (!pairId) return;
    
    try {
      // Use the depth cache endpoint that returns asks/bids with volumeBase + volumeIdr
      const response = await fetch(buildApiUrl(`/exchange/indodax/public/depthCache/${pairId}`), {
        method: 'GET',
        headers: {
          'accept': '*/*'
        },
        credentials: 'include'
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      // Expected shape based on new API:
      // {
      //   pair: "btcidr",
      //   asks: [{ price, volumeBase, volumeIdr }, ...],
      //   bids: [{ price, volumeBase, volumeIdr }, ...]
      // }
      const asks = Array.isArray(data?.asks) ? data.asks : [];
      const bids = Array.isArray(data?.bids) ? data.bids : [];

      const transformedSellOrders = asks.map((order) => {
        const priceNum = parseFloat(order?.price);
        const volumeNum = parseFloat(order?.volumeBase);
        const idrNum = parseFloat(order?.volumeIdr);
        return {
          price: priceNum,
          volume: volumeNum,
          idr: Number.isFinite(idrNum) ? idrNum : priceNum * volumeNum,
        };
      });

      const transformedBuyOrders = bids.map((order) => {
        const priceNum = parseFloat(order?.price);
        const volumeNum = parseFloat(order?.volumeBase);
        const idrNum = parseFloat(order?.volumeIdr);
        return {
          price: priceNum,
          volume: volumeNum,
          idr: Number.isFinite(idrNum) ? idrNum : priceNum * volumeNum,
        };
      });
      
      // Annotate changes for brief flash effect
      const annotateChanges = (prev, next) => {
        const now = Date.now();
        return next.map((n, i) => {
          const p = prev[i];
          let changePrice = 'none';
          let changeVolume = 'none';
          let changedAt = null;
          if (p) {
            if (n.price !== p.price) {
              changePrice = n.price > p.price ? 'up' : 'down';
              changedAt = now;
            }
            if (n.volume !== p.volume) {
              changeVolume = n.volume > p.volume ? 'up' : 'down';
              changedAt = now;
            }
          } else {
            changePrice = 'new';
            changeVolume = 'new';
            changedAt = now;
          }
          return { ...n, changePrice, changeVolume, changedAt };
        });
      };

      const annotatedSells = annotateChanges(prevSellRef.current || [], transformedSellOrders);
      const annotatedBuys = annotateChanges(prevBuyRef.current || [], transformedBuyOrders);

      setSellOrders(annotatedSells);
      setBuyOrders(annotatedBuys);
      prevSellRef.current = annotatedSells;
      prevBuyRef.current = annotatedBuys;
    } catch (error) {
      addLog(`Error fetching orderbook: ${error.message}`);
    }
  };

  // Fetch balance data from API
  const fetchBalanceData = async () => {
    try {
      const response = await fetch(buildApiUrl('/exchange/indodax/getInfo'), {
        method: 'GET',
        headers: {
          'accept': '*/*'
        },
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success && data.return && data.return.balance) {
          const balance = data.return.balance;
          
          // Set IDR balance
          setIdrBalance(parseFloat(balance.idr) || 0);
          
          // Map all coins (excluding IDR), even zero balances
          const coins = Object.entries(balance)
            .filter(([coin]) => coin !== 'idr')
            .map(([coin, amount]) => ({
              coin: coin.toUpperCase(),
              balance: parseFloat(amount) || 0
            }))
            .sort((a, b) => b.coin.localeCompare(a.coin)); // sort alphabetically

          setAvailableCoins(coins);

          // If nothing selected, pick the first coin (alphabetical)
          if (coins.length > 0 && !selectedBlockchain) {
            setSelectedBlockchain(coins[0].coin);
            setVolume(coins[0].balance.toFixed(8));
          }
        }
      }
    } catch (error) {
      console.error('Failed to fetch balance data:', error);
    }
  };

  // Fetch balance data on component mount
  useEffect(() => {
    fetchBalanceData();
  }, []);

  // Live polling for orderbook every 3s (fixed)
  useEffect(() => {
    if (!selectedBlockchain) return;

    const pairId = `${selectedBlockchain.toLowerCase()}idr`;
    let timerId = null;
    let isFetching = false;

    const tick = async () => {
      if (document.hidden) return; // pause when tab hidden
      if (isFetching) return; // avoid overlapping requests
      isFetching = true;
      try {
        await fetchOrderbookData(pairId);
      } catch (_) {
        // swallow; fetchOrderbookData already logs
      } finally {
        isFetching = false;
      }
    };

    // initial fetch and start interval
    tick();
    timerId = window.setInterval(tick, 2000);

    return () => {
      if (timerId) window.clearInterval(timerId);
    };
  }, [selectedBlockchain]);

  // Helper function to add logs
  const addLog = (message) => {
    const timestamp = new Date().toLocaleTimeString();
    const logEntry = { id: Date.now() + Math.random(), timestamp, message };
    setLogs(prev => [logEntry, ...prev].slice(0, 100)); // Keep only last 100 logs
  };

  useEffect(() => {
    if (!isWorkerMode || statusRunning === null) return;
    const previousStatus = prevStatusRef.current;
    prevStatusRef.current = statusRunning;
    isRunningRef.current = statusRunning;
    setIsRunning(statusRunning);
    if (previousStatus === null || previousStatus === statusRunning) return;
    addLog(statusRunning ? 'Spam worker is running' : 'Spam worker stopped');
  }, [isWorkerMode, statusRunning]);

  useEffect(() => {
    if (!isWorkerMode || !isStatusConnected || statusRunning !== null) return;
    requestStatus();
  }, [isWorkerMode, isStatusConnected, requestStatus, statusRunning]);

  useEffect(() => {
    if (!isWorkerMode || !tradeUpdate) return;
    if (tradeUpdate.receivedAt === prevTradeUpdateAtRef.current) return;
    prevTradeUpdateAtRef.current = tradeUpdate.receivedAt;
    const data = tradeUpdate.data;
    if (data && (data.success === 1 || data.success === true)) {
      addLog('Trade worker update: success');
      toast.success('Trade update successful', { id: 'trade-worker-update', duration: 4000 });
      speakNotification('Sell order Successful.');
      return;
    }
    if (data && data.error) {
      addLog(`Trade worker update: ${data.error}`);
      toast.error(`Trade update failed (${data.error})`, { id: 'trade-worker-update' });
      return;
    }
    addLog('Trade worker update received');
  }, [isWorkerMode, tradeUpdate]);

  const speakNotification = (message) => {
    if (typeof window === 'undefined' || !window.speechSynthesis) return;
    try {
      window.speechSynthesis.cancel();
      const utterance = new SpeechSynthesisUtterance(message);
      utterance.lang = 'en-US';
      window.speechSynthesis.speak(utterance);
    } catch (_) {
      // no-op if speech synthesis fails or is blocked
    }
  };

  const buildTradePayload = () => ({
    pair: `${selectedBlockchain.toLowerCase()}_idr`,
    tradeType: 'sell',
    orderType: 'limit',
    price: parseFloat(price),
    amount: volume,
  });

  const submitTradeRequest = async (endpoint) => {
    const response = await csrfFetch(endpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Idempotency-Key': generateIdempotencyKey(),
      },
      credentials: 'include',
      body: JSON.stringify(buildTradePayload()),
    });

    const data = await parseJsonSafely(response);
    return { response, data };
  };

  const cancelWorkerRequest = async () => {
    const response = await csrfFetch(cancelEndpoint, {
      method: 'POST',
      credentials: 'include',
    });

    const data = await parseJsonSafely(response);
    return { response, data };
  };

  const handleStartStop = async () => {
    if (isRunningRef.current) {
      if (isWorkerMode) {
        try {
          const { response, data } = await cancelWorkerRequest();
          if (isResponseSuccess(response, data)) {
            isRunningRef.current = false;
            setIsRunning(false);
            addLog('Spam selling stopped');
            toast.success('Spam worker stopped', { id: 'sell-trade', duration: 4000 });
          } else {
            const errorMessage = data?.error || 'Unknown error';
            addLog(`Failed to stop worker (${errorMessage})`);
            toast.error(`Failed to stop worker (${errorMessage})`, { id: 'sell-trade' });
          }
        } catch (error) {
          addLog('Error stopping worker');
          toast.error('Error stopping worker', { id: 'sell-trade' });
        }
      } else {
        if (timerRef.current) {
          window.clearInterval(timerRef.current);
          timerRef.current = null;
        }
        isRunningRef.current = false;
        setIsRunning(false);
        addLog('Spam selling stopped');
      }
      return;
    }

    // Start spam - validate inputs first
    if (!selectedBlockchain) {
      addLog('Select a coin first');
      return;
    }
    if (!price || !volume) {
      addLog('Cannot start spam: Missing price or volume');
      return;
    }

    const startLog = isWorkerMode
      ? `Starting spam selling: ${selectedBlockchain} - Volume: ${volume}, Price: ${price} IDR`
      : `Starting spam selling: ${selectedBlockchain} - Volume: ${volume}, Price: ${price} IDR, Interval: ${interval}ms`;
    addLog(startLog);

    if (isWorkerMode) {
      isRunningRef.current = true;
      setIsRunning(true);

      try {
        const { response, data } = await submitTradeRequest(startEndpoint);
        if (isResponseSuccess(response, data)) {
          addLog('Spam worker started');
          toast.success('Spam worker started', { id: 'sell-trade', duration: 5000 });
          speakNotification('Spam worker started.');
        } else {
          const errorMessage = data?.error || 'Unknown error';
          toast.error(`Start worker failed (${errorMessage})`, { id: 'sell-trade' });
          addLog(`Start worker failed (${errorMessage})`);
          isRunningRef.current = false;
          setIsRunning(false);
        }
      } catch (error) {
        addLog('Error starting worker');
        toast.error('Error starting worker', { id: 'sell-trade' });
        isRunningRef.current = false;
        setIsRunning(false);
      }
      return;
    }

    isRunningRef.current = true;
    setIsRunning(true);
    speakNotification(`Spam selling started.`);

    const fireRequest = async () => {
      if (!isRunningRef.current) return;
      inFlightRef.current += 1; // count this request as in-flight
      try {
        addLog(`Attempting to sell ${volume} ${selectedBlockchain} at ${price}`);

        const { response, data } = await submitTradeRequest(startEndpoint);

        if (!isRunningRef.current) return; // stopped while request was in-flight
        if (isResponseSuccess(response, data)) {
          addLog('Sell order successful');
          const proceeds = (volume * (parseFloat(price) || 0));
          toast.success(
            <div>
              <p>Sell order placed successfully!</p>
              <p className="text-sm mt-1">Sold {volume} {selectedBlockchain}</p>
              <p className="text-sm">Proceeds: Rp {proceeds.toLocaleString('id-ID')}</p>
            </div>,
            { id: 'sell-trade', duration: 5000 }
          );
          if (timerRef.current) {
            window.clearInterval(timerRef.current);
            timerRef.current = null;
          }
          isRunningRef.current = false;
          setIsRunning(false);
          addLog('Spam selling stopped (order successful)');
          speakNotification('Sell order successful. Spam selling stopped.');
        } else {
          const errorMessage = data?.error || 'Unknown error';
          const rateLimited = response.status === 429
            || /rate|limit|too many/i.test(String(data?.error || ''));
          if (rateLimited) {
            // Doorman said STOP — back off before sending again.
            const retryAfter = parseFloat(response.headers.get('Retry-After'));
            const cooldownMs = Number.isFinite(retryAfter)
              ? retryAfter * 1000
              : RATE_LIMIT_COOLDOWN_MS;
            cooldownUntilRef.current = Date.now() + cooldownMs;
            addLog(`Rate limited — pausing ${Math.round(cooldownMs)}ms before retrying`);
            toast.error('Rate limited by Indodax — backing off', { id: 'sell-trade' });
          } else {
            toast.error(`Sell order failed (${errorMessage})`);
            addLog(`Sell order failed (${errorMessage})`);
          }
        }
      } catch (error) {
        addLog('Error during sell attempt');
      } finally {
        inFlightRef.current -= 1; // request settled (success/fail) -> free a slot
      }
    };

    // Fire immediately then every `delay` ms — does NOT wait for previous response
    const delay = Math.max(60, parseInt(interval) || 1000);
    inFlightRef.current = 0; // reset the in-flight counter for a fresh run
    cooldownUntilRef.current = 0; // clear any leftover backoff
    fireRequest();
    timerRef.current = window.setInterval(() => {
      if (!isRunningRef.current) {
        window.clearInterval(timerRef.current);
        timerRef.current = null;
        return;
      }
      // Backing off after Indodax told us to stop — wait out the cooldown silently.
      if (Date.now() < cooldownUntilRef.current) {
        return;
      }
      // Skip this tick if too many requests are still unresolved. Prevents a pile-up
      // during Indodax lag from later flushing as a burst over the 20 req/s limit.
      if (inFlightRef.current >= MAX_IN_FLIGHT) {
        addLog(`Throttled: ${inFlightRef.current} requests still in flight`);
        return;
      }
      fireRequest();
    }, delay);
  };

  // Stop any running loop on unmount
  useEffect(() => () => {
    if (timerRef.current) {
      window.clearInterval(timerRef.current);
      timerRef.current = null;
    }
    if (isWorkerMode && isRunningRef.current) {
      csrfFetch('/exchange/indodax/trade/worker/cancel', {
        method: 'POST',
        credentials: 'include',
      }).catch(() => {});
    }
    isRunningRef.current = false;
  }, [isWorkerMode]);


  return (
    <>
    <div className="space-y-4">
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Left Column - Controls */}
        <div className="space-y-4">
          {/* Coin Selection */}
          <div className="glass-card p-4">
            <div className="flex justify-between items-center mb-3">
              <h3 className="text-white font-medium">Select Coin to Sell</h3>
              <button
                onClick={fetchBalanceData}
                className="text-xs text-blue-400 hover:text-blue-300"
              >
                Refresh
              </button>
            </div>

            <div className="space-y-2">
              <button
                type="button"
                onClick={() => setShowCoinDialog(true)}
                className="w-full text-left bg-gray-800 border border-gray-700 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 flex items-center justify-between"
              >
                <div>
                  <p className="text-white font-medium">
                    {selectedBlockchain || 'Select a coin'}
                  </p>
                </div>
                <span className="text-gray-400 text-xs">Tap to change</span>
              </button>
            </div>
          </div>

          {/* Spam Settings */}
          <div className="glass-card p-4">
            <h3 className="text-white font-medium mb-4">
              Spam sell - {selectedBlockchain || 'Select coin'}
            </h3>
            
            <div className="space-y-4">
              <div>
                <label className="block text-gray-400 text-sm mb-2">Interval (ms)</label>
                <input
                  type="number"
                  value={isWorkerMode ? '' : interval}
                  min={60}
                  disabled={isWorkerMode}
                  placeholder={isWorkerMode ? 'Server controlled' : undefined}
                  onChange={(e) => setInterval(Math.max(60, Number(e.target.value)))}
                  className="w-full px-4 py-2 bg-gray-800 border border-gray-700 rounded-md text-white focus:outline-none focus:border-blue-500 disabled:cursor-not-allowed disabled:opacity-60"
                />
                <div className="mt-2 bg-gray-800 rounded-md p-2">
                  <input
                    type="range"
                    min="60"
                    max="1000"
                    value={interval}
                    disabled={isWorkerMode}
                    onChange={(e) => setInterval(Math.max(60, Number(e.target.value)))}
                    className="w-full accent-blue-400 disabled:cursor-not-allowed disabled:opacity-60"
                    style={{ accentColor: '#60a5fa' }}
                  />
                </div>
              </div>

              <div>
                <label className="block text-gray-400 text-sm mb-2">
                  Volume ({selectedBlockchain || 'COIN'})
                </label>
                <input
                  type="text"
                  value={volume}
                  onChange={(e) => setVolume(e.target.value)}
                  className="w-full px-4 py-2 bg-gray-800 border border-gray-700 rounded-md text-white focus:outline-none focus:border-blue-500"
                />
                {selectedBlockchain && (
                  <p className="text-xs text-gray-500 mt-1">
                    Available: {availableCoins.find(c => c.coin === selectedBlockchain)?.balance?.toFixed(8) || '0'} {selectedBlockchain}
                  </p>
                )}
              </div>

              <div>
                <label className="block text-gray-400 text-sm mb-2">Price (IDR)</label>
                <input
                  type="text"
                  value={price}
                  onChange={(e) => setPrice(e.target.value)}
                  placeholder="Enter price or click from orderbook"
                  className="w-full px-4 py-2 bg-gray-800 border border-gray-700 rounded-md text-white focus:outline-none focus:border-blue-500"
                />
                <p className="text-gray-500 text-xs mt-1">Rate: 16/8/100</p>
              </div>

              <button
                onClick={handleStartStop}
                className={cn(
                  "w-full py-3 rounded-md font-medium flex items-center justify-center space-x-2 transition-colors",
                  isRunning 
                    ? "bg-red-600 hover:bg-red-700 text-white"
                    : "bg-green-600 hover:bg-green-700 text-white"
                )}
              >
                {isRunning ? (
                  <>
                    <Square className="h-5 w-5" />
                    <span>Stop Spam</span>
                  </>
                ) : (
                  <>
                    <Play className="h-5 w-5" />
                    <span>Start Spam</span>
                  </>
                )}
              </button>

              <button 
                onClick={() => setShowLogs(!showLogs)}
                className="w-full py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-md text-sm transition-colors"
              >
                {showLogs ? 'Hide Logs' : 'Show Logs'} ({logs.length})
              </button>
            </div>
          </div>

          {/* Logs Display */}
          {showLogs && (
            <div className="glass-card p-4">
              <h3 className="text-white font-medium mb-4">Spam Logs</h3>
              <div className="bg-black rounded-md p-3 max-h-96 overflow-y-auto">
                {logs.length === 0 ? (
                  <p className="text-gray-500 text-sm">No logs yet...</p>
                ) : (
                  <div className="space-y-1">
                    {logs.map(log => (
                      <div key={log.id} className="text-xs font-mono">
                        <span className="text-gray-400">{log.timestamp}</span>
                        <span className="text-green-400 ml-2">{log.message}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Middle Column - USDT/IDR Rate */}
        <div className="glass-card p-6 flex flex-col items-center justify-center">
          <p className="text-gray-400 text-sm mb-2">USDT/IDR</p>
          <p className="text-4xl font-bold text-white mb-1">
            {coinData?.bid ? formatCryptoIDR(coinData.bid) : 'Loading...'}
          </p>
          <p className="text-gray-500 text-sm">1 USDT = {coinData?.bid?.toLocaleString('id-ID') || 'N/A'} IDR</p>
        </div>

        {/* Right Column - IDR Balance */}
        <div className="glass-card p-6 flex flex-col items-center justify-center">
          <p className="text-gray-400 text-sm mb-2">IDR BALANCE</p>
          <p className="text-3xl font-bold text-white mb-2">{formatIDRWithDecimals(idrBalance, 3)}</p>
          <div className="flex items-center space-x-2">
            <span className="text-gray-400 text-sm">
              {availableCoins.length} coin{availableCoins.length !== 1 ? 's' : ''} available
            </span>
          </div>
        </div>
      </div>

      {/* Order Books */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Sell Orders */}
        <div className="glass-card p-4">
          <h3 className="text-white font-medium mb-4">Sell Orders</h3>
          <div className="space-y-1">
            <div className="grid grid-cols-3 gap-4 text-xs text-gray-400 uppercase tracking-wider pb-2 border-b border-gray-700">
              <div>Price (IDR)</div>
              <div className="text-right">{selectedBlockchain || 'COIN'} Volume</div>
              <div className="text-right">IDR Volume</div>
            </div>
            <div className="space-y-1 max-h-96 overflow-y-auto">
              {sellOrders.map((order, index) => (
                <div 
                  key={index} 
                  className={cn(
                    "grid grid-cols-3 gap-4 py-1.5 cursor-pointer rounded transition-colors",
                    (() => {
                      const recent = order.changedAt && (Date.now() - order.changedAt) < 1000;
                      return recent ? 'bg-blue-900/20 animate-pulse' : 'hover:bg-gray-800';
                    })()
                  )}
                  onClick={() => setPrice(order.price.toString())}
                >
                  <div className="text-red-500 font-mono text-sm">
                    {formatWithDots(order.price)}
                  </div>
                  <div className="text-white text-right font-mono text-sm">
                    {formatWithDots(order.volume, { maximumFractionDigits: 8 })}
                  </div>
                  <div className="text-gray-300 text-right font-mono text-sm">
                    {formatWithDots(order.idr)}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Buy Orders */}
        <div className="glass-card p-4">
          <h3 className="text-white font-medium mb-4">Buy Orders</h3>
          <div className="space-y-1">
            <div className="grid grid-cols-3 gap-4 text-xs text-gray-400 uppercase tracking-wider pb-2 border-b border-gray-700">
              <div>Price (IDR)</div>
              <div className="text-right">{selectedBlockchain || 'COIN'} Volume</div>
              <div className="text-right">IDR Volume</div>
            </div>
            <div className="space-y-1 max-h-96 overflow-y-auto">
              {buyOrders.map((order, index) => (
                <div 
                  key={index} 
                  className={cn(
                    "grid grid-cols-3 gap-4 py-1.5 cursor-pointer rounded transition-colors",
                    (() => {
                      const recent = order.changedAt && (Date.now() - order.changedAt) < 1000;
                      return recent ? 'bg-blue-900/20 animate-pulse' : 'hover:bg-gray-800';
                    })()
                  )}
                  onClick={() => setPrice(order.price.toString())}
                >
                  <div className="text-green-500 font-mono text-sm">
                    {formatWithDots(order.price)}
                  </div>
                  <div className="text-white text-right font-mono text-sm">
                    {formatWithDots(order.volume, { maximumFractionDigits: 8 })}
                  </div>
                  <div className="text-gray-300 text-right font-mono text-sm">
                    {formatWithDots(order.idr)}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>

    {showCoinDialog && (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4">
        <div className="bg-gray-900 border border-gray-800 rounded-xl shadow-2xl max-w-lg w-full">
          <div className="flex items-center justify-between px-4 py-3 border-b border-gray-800">
            <div>
              <h3 className="text-lg font-semibold text-white">Select Coin to sell</h3>
            </div>
            <button
              onClick={() => {
                setShowCoinDialog(false);
                setCoinSearch('');
              }}
              className="text-gray-400 hover:text-white p-2 rounded-md hover:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-blue-500"
              aria-label="Close coin selector"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          <div className="p-4 space-y-3">
            <input
              type="text"
              value={coinSearch}
              onChange={(e) => setCoinSearch(e.target.value)}
              className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Search coin (e.g., BTC)"
            />

            <div className="max-h-72 overflow-y-auto divide-y divide-gray-800">
              {availableCoins
                .filter((coin) =>
                  coin.coin.toLowerCase().includes(coinSearch.trim().toLowerCase())
                )
                .map((coin) => (
                  <button
                    key={coin.coin}
                    onClick={() => {
                      setSelectedBlockchain(coin.coin);
                      setVolume((coin.balance || 0).toFixed(8));
                      setShowCoinDialog(false);
                      setCoinSearch('');
                    }}
                    className={`w-full text-left px-3 py-3 hover:bg-gray-800 flex items-center justify-between ${
                      selectedBlockchain === coin.coin ? 'bg-gray-800' : ''
                    }`}
                  >
                    <div>
                      <p className="text-white font-medium">{coin.coin}</p>
                      <p className="text-xs text-gray-400">
                        Balance: {coin.balance?.toFixed(8) || '0'}
                      </p>
                    </div>
                  </button>
                ))}
              {availableCoins.filter((coin) =>
                coin.coin.toLowerCase().includes(coinSearch.trim().toLowerCase())
              ).length === 0 && (
                <p className="text-sm text-gray-500 px-3 py-2">No coins match your search.</p>
              )}
            </div>
          </div>
        </div>
      </div>
    )}
    </>
  );
};

export default SpamSell;
